import React, { Dispatch, SetStateAction, useState } from 'react';
import styles from './AppointmentFullCalendar.module.css';
import { AnyIfEmpty, useDispatch } from 'react-redux';
import { AppDispatch } from '../../../redux/rootReducer';
import moment from 'moment';
import PhoneInput from 'react-phone-input-2';
import { downloadGuestCard } from '../../../redux/guest-card/actions/guestCard.action';
import Loader from '../../common/loader/Loader';
import { hasPermission } from '../../auth/login/Login';
import iconClose from '../../../images/icon-close.svg';
import { getLocationTimeZone } from '../../../constants/constants';

interface props {
    setOpen: (active: boolean) => void;
    locationId: string;
    calendarInfo: AnyIfEmpty<object>;
    setShowTimeStamp: Dispatch<SetStateAction<boolean>>;
    setShowDeleteModal: Dispatch<SetStateAction<boolean>>;
}

const AppointmentPopup = ({ setOpen, locationId, calendarInfo, setShowTimeStamp, setShowDeleteModal }: props) => {
    const dispatch: AppDispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const canDeleteAppointment = hasPermission('Self-Guided Tours', 'Appointments', 'delete');

    const viewTimeStamp = () => {
        setShowTimeStamp(true);
        setOpen(false);
    };

    const onDownloadGuestCard = () => {
        setIsLoading(true);
        dispatch(downloadGuestCard(calendarInfo?.appointmentId, setIsLoading));
    };

    return (
        <div className={`${styles.eventInfo} event-info`}>
            {isLoading && <Loader />}
            <div className={styles.eventHeader}>
                <div className={styles.titleWrap}>
                    <h2>Appointment</h2>
                    <div role="button" className={styles.closeIcon} onClick={() => setOpen(false)}>
                        <img src={iconClose} alt="Close" className={styles.iconRemove} />
                    </div>
                </div>
            </div>
            <div className={`${styles.eventBody} appointmentPopupBody`}>
                <div className={styles.eventData}>
                    <label className={styles.eventTitle}>Property</label>
                    <p className={styles.eventDesc}>{calendarInfo.locationName}</p>
                </div>
                <div className={styles.eventData}>
                    <label className={styles.eventTitle}>Unit Name</label>
                    <p className={styles.eventDesc}>{calendarInfo.unitYardiId ? calendarInfo.unitYardiId : calendarInfo.unitName}</p>
                </div>
                <div className={styles.eventData}>
                    <label className={styles.eventTitle}>Visitor Name</label>
                    <p className={styles.eventDesc}>{calendarInfo.firstName + ' ' + calendarInfo.lastName}</p>
                </div>
                <div className={styles.eventData}>
                    <label className={styles.eventTitle}>Locker Number</label>
                    <p className={styles.eventDesc}>{calendarInfo.locker_number}</p>
                </div>
                <div className={styles.eventData}>
                    <label className={styles.eventTitle}>Time</label>
                    <p className={styles.eventDesc}>{`${moment(calendarInfo.appointmentStartTime, 'h:mm A').format('h:mm A')} - 
                        ${
                            calendarInfo.appointmentEndTime.trim() === '23:59'
                                ? moment(calendarInfo.appointmentEndTime, 'h:mm A').add(1, 'minute').format('h:mm A')
                                : moment(calendarInfo.appointmentEndTime, 'h:mm A').format('h:mm A')
                        }`}</p>
                </div>
                <div className={styles.eventData}>
                    <label className={styles.eventTitle}>Date</label>
                    <p className={styles.eventDesc}>{moment(calendarInfo.appointmentDate).utc().locale('en').format('MM-DD-YYYY')}</p>
                </div>
                {calendarInfo?.lockerName?.toLowerCase() !== 'bms' && (
                    <div className={styles.eventData}>
                        <label className={styles.eventTitle}>Access Code</label>
                        <p className={styles.eventDesc}>{calendarInfo.code}</p>
                    </div>
                )}
                <div className={styles.eventData}>
                    <label className={styles.eventTitle}>Email</label>
                    <p className={styles.eventDesc}>{calendarInfo.email}</p>
                </div>

                <div className={styles.eventData}>
                    <label className={styles.eventTitle}>Desired No. of Bedrooms</label>
                    <p className={styles.eventDesc}>
                        {calendarInfo.desiredNumBedrooms === '0' ? 'Studio' : calendarInfo.desiredNumBedrooms}
                    </p>
                </div>
                <div className={`${styles.eventData} eventPhone`}>
                    <label className={styles.eventTitle}>Phone Number</label>
                    <div className={`${styles.eventDesc} eventDesc`}>
                        <PhoneInput value={String(`+${calendarInfo.countryCode}${calendarInfo.phoneNumber}`)} disabled />
                    </div>
                </div>
                <div className={styles.eventData}>
                    <label className={styles.eventTitle}>Desired Move in Date</label>
                    <p className={styles.eventDesc}>
                        {calendarInfo.expectedMoveInDate && moment(calendarInfo.expectedMoveInDate).utc().locale('en').format('MM-DD-YYYY')}
                    </p>
                </div>
                <div className={styles.eventData}>
                    <label className={styles.eventTitle}>Timezone</label>
                    <p className={styles.eventDesc}>{getLocationTimeZone(calendarInfo.timeZone)}</p>
                </div>
                <div className={styles.eventData}>
                    <label className={styles.eventTitle}>Lead Source</label>
                    <p className={styles.eventDesc}>{calendarInfo?.pmsLeadSorce?.lead_name}</p>
                </div>
                <div className={styles.eventData}>
                    <label className={styles.eventTitle}>Account Created Date/Time</label>
                    <p className={styles.eventDesc}>
                        {calendarInfo.created_at
                            ? moment(calendarInfo.created_at).tz(calendarInfo?.timeZone).format('MM-DD-YYYY hh:mm A')
                            : ''}
                    </p>{' '}
                </div>
            </div>

            {(calendarInfo?.hasAdminDeleted === 1 || calendarInfo?.deletedAt !== null) && (
                <div className={styles?.deleteEventInfo}>
                    <p>
                        This appointment has been deleted by <span>{calendarInfo?.AppointmentDetails?.appointmentDeleteByName}</span> at{' '}
                        <span>{moment(calendarInfo?.AppointmentDetails?.appointmentDeletedAt).local().format('MM-DD-YYYY hh:mm A')}.</span>
                    </p>
                    <p>
                        Reason: <span>{`${calendarInfo?.AppointmentDetails?.reason}`}</span>
                    </p>
                </div>
            )}
            <div className={styles.getMoreDetails}>
                {canDeleteAppointment && calendarInfo?.hasAdminDeleted !== 1 && calendarInfo?.deletedAt === null && (
                    <p onClick={() => setShowDeleteModal(true)}>Delete this Appointment</p>
                )}
                {calendarInfo?.photoCount > 0 && (
                    <p className={styles.viewTimeStamp} onClick={viewTimeStamp}>
                        View All Timestamp
                    </p>
                )}
                {calendarInfo?.lockerName?.toLowerCase() !== 'bms' && (
                    <p className={styles.viewTimeStamp} onClick={onDownloadGuestCard}>
                        Download Guest card
                    </p>
                )}
            </div>
        </div>
    );
};

export default AppointmentPopup;
